import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'

import { Col, Layout, Menu, Modal, Row } from 'antd'
import useAuthorization from 'core/hooks/useAuthorization'
import _ from 'lodash'
import PropTypes from 'prop-types'
import * as R from 'ramda'

import * as Icons from '@ant-design/icons'

import useRequestHook from '../hooks/useRequestHook'
import { AppContext } from '../index'

export default function LayoutComponent({
  theme = 'default',
  siderActiveKey = '',
  siderCollapsed,
  siderTheme,
  isLogin = false,
  ...props
}) {
  const {
    responsive: { isMobile }
  } = useContext(AppContext)
  const [menuCollapsed, setMenuCollapsed] = useState(siderCollapsed || isMobile)

  const showSider = useMemo(() => {
    console.log('theme', theme)
    if (isLogin) return false
    return isMobile || theme == 'default'
  }, [theme, isMobile])

  const contentStyle = useMemo(() => {
    let paddingLeft = isLogin ? 15 : 0
    if (showSider) paddingLeft = menuCollapsed ? 70 : isMobile ? 70 : 220
    let contentStyle = {
      minHeight: '100vh',
      padding: 15,
      paddingTop: 80,
      paddingLeft: paddingLeft
    }

    if (theme == 'clean-centered') {
      contentStyle = Object.assign(contentStyle, {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
      })
    }

    return contentStyle
  }, [theme, menuCollapsed, showSider])

  useEffect(() => {
    if (isMobile && !menuCollapsed) {
      setMenuCollapsed(true)
    }
  }, [isMobile])

  return (
    <Layout>
      <Header
        menuCollapsed={menuCollapsed}
        setMenuCollapsed={setMenuCollapsed}
        isLogin={isLogin}
      />

      <Layout>
        {showSider && (
          <Sider
            siderActiveKey={siderActiveKey}
            siderTheme={siderTheme || 'light'}
            collapsed={menuCollapsed}
            onCollapsedChange={setMenuCollapsed}
          />
        )}
        <Layout.Content style={contentStyle}>{props.children}</Layout.Content>
      </Layout>

      <Footer />
    </Layout>
  )
}

LayoutComponent.propTypes = {
  theme: PropTypes.oneOf(['default', 'clean', 'clean-centered']).isRequired,
  siderActiveKey: PropTypes.string,
  isLogin: PropTypes.bool
}

/***************************************************************************************************** */

function Header(props) {
  const { isLogin } = props
  const {
    getConfig,
    responsive: { isMobile }
  } = useContext(AppContext)
  const logo = getConfig('global_logo')
  const title = getConfig('global_title')

  return (
    <Layout.Header style={{ position: 'fixed', zIndex: 999, width: '100%' }}>
      <Row>
        <Col
          span={6}
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            color: '#fff',
            textTransform: 'uppercase',
            fontWeight: 20
          }}
        >
          {logo ? (
            <img src={logo} alt={title} title={title} style={{ height: 40 }} />
          ) : (
            <span>{title}</span>
          )}
        </Col>
        <Col span={18}>
          {isMobile ? (
            <>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-end'
                }}
              >
                <MenuSecondary forceHorizontalMenu={true} isLogin={isLogin} />
              </div>
            </>
          ) : (
            <MenuSecondary isLogin={isLogin} />
          )}
        </Col>
      </Row>
    </Layout.Header>
  )
}

function Footer() {
  const { getConfig } = useContext(AppContext)
  const footer = getConfig('global_footer')

  return (
    <Layout.Footer style={{ textAlign: 'center' }}>
      <div dangerouslySetInnerHTML={{ __html: footer }} />
    </Layout.Footer>
  )
}

function Sider(props) {
  const { siderActiveKey, siderTheme, collapsed, onCollapsedChange } = props
  const {
    responsive: { isMobile }
  } = useContext(AppContext)

  return (
    <Layout.Sider
      collapsible
      collapsed={collapsed}
      onCollapse={onCollapsedChange}
      defaultCollapsed
      width={200}
      collapsedWidth={55}
      theme={siderTheme}
      style={{
        position: 'fixed',
        top: 64,
        height: `calc(100% - 64px)`,
        zIndex: 999
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          height: '100%'
        }}
      >
        <MenuPrimary activeKey={siderActiveKey} theme={siderTheme} />
        {isMobile && <MenuSecondary theme={siderTheme} />}
      </div>
    </Layout.Sider>
  )
}

function MenuPrimary(props) {
  const { activeKey, theme } = props
  const { getConfig } = useContext(AppContext)
  const request = useRequestHook()
  const intl = useIntl()
  const { can } = useAuthorization()

  const sidebarItems = useMemo(() => {
    let items = []

    items = getConfig('sidebar_items').filter(
      (i) => !i.permission || can(i.permission)
    )

    items = items.map((i) => {
      i.label = intl.formatMessage({ id: i.label })
      return i
    })

    return items
  }, [])

  const onClickUrl = (path) => {
    if (path.includes('/maintenances/') && !localStorage.getItem('jwt')) {
      Modal.warning({
        closable: true,
        title: intl.formatMessage({ id: 'warning.no_vidim_token_modal.title' }),
        content: intl.formatMessage({ id: 'warning.no_vidim_token_modal.description' }),
        okText: intl.formatMessage({ id: 'warning.no_vidim_token_modal.cta.logout' }),
        okButtonProps: {
          danger: true,
          icon: <Icons.LogoutOutlined />
        },
        cancelText: intl.formatMessage({ id: 'warning.no_vidim_token_modal.cta.cancel' }),
        okCancel: true,
        onOk: () => {
          request.send('destroy_user_session').then(() => {
            localStorage.clear()
            window.location.reload()
          })
        },
        onCancel: () => { },
        onClose: () => { }
      })
    } else {
      window.location.href = path
    }
  }

  // CUSTOM EDIT FOR DTI
  const sidebarItemsFiltered = useMemo(() => {
    let _sidebarItems = sidebarItems

    const hideMainteinance =
      document
        .querySelector('meta[name="current-user-manteinance"]')
        ?.getAttribute('content') == '0'
    if (hideMainteinance) {
      _sidebarItems = sidebarItems.filter((item) => {
        return item.name != 'microservices_maintenance'
      })
    }

    const hideContracts =
      document
        .querySelector('meta[name="current-user-contracts"]')
        ?.getAttribute('content') == '0'
    if (hideContracts) {
      _sidebarItems = _sidebarItems.filter((item) => {
        return item.name != 'contracts'
      })
    }

    return _sidebarItems
  }, [sidebarItems])
  //

  const renderMenuItems = () =>
    sidebarItemsFiltered.map((sidebarItem) => {
      const IconItem = sidebarItem.icon ? Icons[sidebarItem.icon] : null

      return {
        key: sidebarItem.name,
        icon: IconItem ? (
          <IconItem />
        ) : sidebarItem.icon ? (
          <span dangerouslySetInnerHTML={{ __html: sidebarItem.icon }} />
        ) : null,
        label: sidebarItem.label,
        onClick: () => onClickUrl(sidebarItem.url)
      }
    })

  return (
    <Menu
      theme={theme || 'dark'}
      mode='vertical'
      selectedKeys={[activeKey]}
      items={renderMenuItems()}
    />
  )
}

function MenuSecondary(props) {
  const { theme, forceHorizontalMenu } = props
  const {
    getRoute,
    getAvailableLocales,
    responsive: { isMobile }
  } = useContext(AppContext)

  const availableLocales = getAvailableLocales()
  const request = useRequestHook()
  const intl = useIntl()

  const availableWaterUnits = ['l', 'g', 'once']
  const availableTemperatureUnits = ['celsius', 'fahrenheit']

  const onChangeLocale = (newLocale) => {
    request
      .send('matilda_core_helper_update_session_locale', {
        params: { locale: newLocale }
      })
      .then(() => {
        const currentLocale = localStorage.getItem('i18nextLng')
        if (currentLocale != newLocale)
          localStorage.setItem('i18nextLng', newLocale)
        window.location.reload()
      })
      .catch(console.error)
  }

  const onChangeUnit = (newUnit) => {
    request
      .send('matilda_core_update_water_unit', {
        params: { water_unit: newUnit }
      })
      .then(() => {
        const currentUnit = localStorage.getItem('waterUnit')
        if (currentUnit != newUnit) localStorage.setItem('waterUnit', newUnit)

        window.location.reload()
      })
      .catch(console.error)
  }

  const onChangeTemperatureUnit = (newUnit) => {
    request
      .send('matilda_core_update_temperature_unit', {
        params: { temperature_unit: newUnit }
      })
      .then(() => {
        const currentUnit = localStorage.getItem('temperatureUnit')
        if (currentUnit != newUnit)
          localStorage.setItem('temperatureUnit', newUnit)
        window.location.reload()
      })
      .catch(console.error)
  }
  const onClickUrl = (path) => {
    window.location.href = path.path
  }

  const onLogout = () => {
    request.send('destroy_user_session').then(() => {
      localStorage.clear()
      window.location.reload()
    })
  }

  const manualUrl = document
    .querySelector('meta[name="manual-url"]')
    ?.getAttribute('content')
  const onDownloadManual = () => {
    const link = document.createElement('a')
    link.href = manualUrl
    link.setAttribute('download', '')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const { can } = useAuthorization()

  const languageItems = () => {
    return availableLocales.map((availableLocale) => {
      return {
        key: availableLocale,
        label: availableLocale.toUpperCase(),
        onClick: () => onChangeLocale(availableLocale)
      }
    })
  }

  const profileItems = () => [
    {
      key: 'profile_account_settings',
      label: intl.formatMessage({ id: 'profile.account_settings' }),
      onClick: () => onClickUrl(getRoute('profile_index_view'))
    },
    {
      key: 'profile_logout',
      label: intl.formatMessage({ id: 'cta.logout' }),
      onClick: () => onLogout()
    }
  ]

  const unitItems = () => [
    {
      key: 'volumes',

      label: intl.formatMessage({ id: 'measure_units.volume' }),
      type: 'group',
      children: _.map(availableWaterUnits, (unit) => ({
        key: unit,
        label: intl.formatMessage({ id: `measure_units.${unit}` }),
        onClick: () => onChangeUnit(unit),
        style: {
          margin: 0,
          width: '100%',
          borderRadius: 0
        }
      }))
    },
    {
      key: 'temperature',

      label: intl.formatMessage({ id: 'measure_units.temperature' }),
      type: 'group',
      children: _.map(availableTemperatureUnits, (unit) => ({
        key: unit,
        label: intl.formatMessage({ id: `measure_units.${unit}` }),
        onClick: () => onChangeTemperatureUnit(unit),
        style: {
          margin: 0,
          width: '100%',
          borderRadius: 0
        }
      }))
    }
  ]

  const actionItems = () =>
    [
      {
        key: 'profile',
        icon: <Icons.UserOutlined />,
        label: intl.formatMessage({ id: 'profile.translation' }),
        children: profileItems()
      },
      manualUrl && {
        key: 'manual',
        icon: <Icons.BookOutlined />,
        label: intl.formatMessage({ id: 'manual' }),
        onClick: () => onDownloadManual()
      },
      {
        key: 'measure_unit',
        icon: <Icons.FunnelPlotOutlined />,
        label: intl.formatMessage({ id: 'measure_units.translation' }),
        children: unitItems()
      }
    ].filter(Boolean)

  return (
    <Menu
      theme={theme || 'dark'}
      mode={
        forceHorizontalMenu
          ? 'horizontal'
          : isMobile
            ? 'vertical'
            : 'horizontal'
      }
      style={{
        width: '100%',
        display: 'flex',
        justifyContent: 'flex-end',
        flexDirection: forceHorizontalMenu ? 'row' : isMobile ? 'column' : 'row'
      }}
      selectedKeys={[
        R.defaultTo('celsius', localStorage.getItem('temperatureUnit')),
        R.defaultTo('l', localStorage.getItem('waterUnit')),
        R.defaultTo('it', localStorage.getItem('i18nextLng'))
      ]}
      items={[
        ...(can('profile.read') ? actionItems() : []),
        {
          key: 'language',
          icon: <Icons.FlagOutlined />,
          label: intl.formatMessage({ id: 'lang' }),
          children: languageItems()
        }
      ]}
    />
  )
}
